/* eslint-disable @typescript-eslint/prefer-promise-reject-errors -- validator system uses this weird approach */

import "./currencyInput.scss";
import { isNotEmptyOrWhiteSpace } from "utils/strings";
import { preventInputMouseWheel } from "utils/events";
import { stringFromDollars } from "utils/currency";
import type { InputNumberProps, InputProps } from "antd";
import type { RuleObject } from "antd/es/form";

const sharedProps = {
    inputMode: "decimal",
    min: 0,
    max: 100000,
    onWheel: preventInputMouseWheel,
    placeholder: "0.00",
    step: 0.01,
    type: "number",
} satisfies InputProps;

/** Default Input component props for handling currency. */
export const currencyInputProps = {
    ...sharedProps,
    classNames: {
        input: "currency-input",
    },
} satisfies InputProps;

/** Default InputNumber component props for handling currency. */
export const currencyInputNumberProps = {
    ...sharedProps,
    className: "currency-input",
    controls: false,
    precision: 2,
} satisfies InputNumberProps<number>;

// Pre-compile regular expression, used to check valid amount values
// see tests for valid/invalid examples
const validAmountRegex = new RegExp(/^-?\d*(\.\d{1,2})?$/);

/**
 * AntD Form validator that will check that the number entered is a valid payment amount.
 *
 * This form is appropriate for use with `<Input>`, which stores the data as a string.
 */
export function validAmount(rule: RuleObject, value: unknown) {
    if (!isNotEmptyOrWhiteSpace(value)) return Promise.reject("Please enter an amount");
    if (!validAmountRegex.test(value))
        return Promise.reject("Amount can only include numbers and decimals (e.g. 10.00)");

    const amount = parseFloat(value);
    return validAmountNumber(rule, amount);
}

/**
 * AntD Form validator that will check that the number entered is a valid payment amount.
 *
 * This form is appropriate for use with `<InputNumber<number>>`, which stores the data as a number.
 */
export function validAmountNumber(rule: RuleObject, value: unknown) {
    if (typeof value !== "number") return Promise.reject("Please enter an amount");

    const min = rule.min ?? 0;
    if (value < min) return Promise.reject(`Please enter an amount of at least ${stringFromDollars(min)}`);
    if (rule.max && value > rule.max)
        return Promise.reject(`Please enter an amount no more than ${stringFromDollars(rule.max)}`);

    return Promise.resolve();
}
